/**
 * ProposeFeatureSheet (T35.7.1) — the propose-a-feature bottom sheet from the
 * Feedback tab. The real version of the prototype's `#propose-sheet`.
 *
 * The user types their idea; a live similar-search (debounced, published-only via
 * the /search endpoint) surfaces existing matches under "Already suggested —
 * upvote or back it instead of adding a duplicate:". Each match renders as the
 * SAME full interactive idea card the published list uses (`FeatureIdeaCardView`)
 * — title · description · provenance · 👍/👎 · why reveal · back — so the user
 * engages a match (vote → why → back) instead of minting a duplicate (the proto's
 * `proposeCard`, "fully functional here"). "Add as a new idea" proposes a brand-
 * new (held, new-suggestion) idea and shows the "Idea added" done state, whose own
 * "Back your request" offer reuses the T35.7.2 backing surface.
 *
 * Standalone feature (ADR-012): search / propose are owned here (`./api`); the
 * backing dialog (`BackIdeaDialog`) composes from `features/subscriptions/` (T35.7.2 Part A).
 *
 * Token-only (CP5); mobile-first; ≥44px targets. Reuses the shared `Modal` in its
 * `sheet` variant (bottom-anchored, Android-Back aware via the overlay registry).
 */

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Modal } from '../../components/Modal';
import {
  searchFeatureIdeas,
  proposeFeatureIdea,
  type IdeaSearchMatch,
  type VoteDirection,
} from './api';
import { FeatureIdeaCardView } from './FeatureIdeaCardView';
import { BackIdeaDialog } from './BackIdeaDialog';

const SEARCH_DEBOUNCE_MS = 250;

const subStyle: CSSProperties = {
  margin: '0 0 var(--space-3)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-muted)',
  lineHeight: 1.45,
};

const inputStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  font: 'inherit',
  fontSize: 'var(--font-size-base)',
  padding: 'var(--space-3)',
  border: '0.0625rem solid var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-bg-primary)',
  resize: 'vertical',
  minHeight: '4rem',
};

const resultsHeadStyle: CSSProperties = {
  margin: 'var(--space-3) 0 var(--space-2)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-secondary)',
};

// The matches stack — full idea cards, one per match (proto's `proposeCard`).
const matchListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
};

const addRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  flexWrap: 'wrap',
  marginTop: 'var(--space-3)',
};

const addHintStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
};

const addBtnStyle: CSSProperties = {
  minHeight: '2.75rem',
  border: 0,
  background: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  borderRadius: 'var(--radius-md)',
  padding: '0 var(--space-4)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const footStyle: CSSProperties = {
  marginTop: 'var(--space-4)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  lineHeight: 1.45,
};

const doneStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-4) 0',
  textAlign: 'center',
};

const doneTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-md)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-text-primary)',
};

const doneBodyStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-muted)',
};

const doneCloseStyle: CSSProperties = {
  marginTop: 'var(--space-3)',
  minHeight: '2.75rem',
  border: 0,
  background: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  borderRadius: 'var(--radius-md)',
  padding: '0 var(--space-5)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

// The propose-done "back your request" offer (the proto's `.donate-offer`).
const donateOfferStyle: CSSProperties = {
  marginTop: 'var(--space-4)',
  padding: 'var(--space-3)',
  border: '0.0625rem solid var(--color-accent-primary)',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-accent-subtle)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-1)',
  textAlign: 'left',
};

const doHeadStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-accent-primary)',
};

const doDescStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.45,
};

const backOpenStyle: CSSProperties = {
  alignSelf: 'flex-start',
  marginTop: 'var(--space-1)',
  minHeight: '2.75rem',
  border: 0,
  background: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  borderRadius: 'var(--radius-pill)',
  padding: '0 var(--space-4)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

export function ProposeFeatureSheet({
  open,
  onClose,
  onProposed,
  onUpvoted,
}: {
  open: boolean;
  onClose: () => void;
  /** Fired after a new idea is proposed (so the list can refresh). */
  onProposed: () => void;
  /** Fired after a match is voted on (so the published list can refresh). */
  onUpvoted: () => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const [matches, setMatches] = useState<IdeaSearchMatch[]>([]);
  const [searched, setSearched] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const searchTimer = useRef<number | null>(null);
  // The just-proposed idea (its id drives "back your request"), + backing state.
  const [proposedId, setProposedId] = useState<string | null>(null);
  const [backOpen, setBackOpen] = useState(false);
  const [backBusy, setBackBusy] = useState(false);

  // Reset the sheet each time it opens.
  useEffect(() => {
    if (open) {
      setText('');
      setMatches([]);
      setSearched(false);
      setDone(false);
      setBusy(false);
      setProposedId(null);
      setBackOpen(false);
      setBackBusy(false);
    }
  }, [open]);

  // Cancel any pending debounce on unmount so it never fires post-teardown.
  useEffect(
    () => () => {
      if (searchTimer.current != null) window.clearTimeout(searchTimer.current);
    },
    [],
  );

  // Live similar-search on a short debounce.
  function onInput(value: string): void {
    setText(value);
    if (searchTimer.current != null) window.clearTimeout(searchTimer.current);
    const q = value.trim();
    if (q === '') {
      setMatches([]);
      setSearched(false);
      return;
    }
    searchTimer.current = window.setTimeout(() => {
      searchFeatureIdeas(q)
        .then((m) => {
          setMatches(m);
          setSearched(true);
        })
        .catch(() => {
          setMatches([]);
          setSearched(true);
        });
    }, SEARCH_DEBOUNCE_MS);
  }

  // A match is a full idea card; voting on it reflects the new count + vote in
  // place (so the why/back reveal opens), and refreshes the published list.
  function onMatchVoted(
    id: string,
    result: { my_vote: VoteDirection | null; upvotes: number },
  ): void {
    setMatches((prev) =>
      prev.map((m) => (m.id === id ? { ...m, my_vote: result.my_vote, upvotes: result.upvotes } : m)),
    );
    onUpvoted();
  }

  async function submit(): Promise<void> {
    if (busy || text.trim() === '') return;
    setBusy(true);
    try {
      const result = await proposeFeatureIdea(text.trim());
      setProposedId(result.id);
      setDone(true);
      onProposed();
    } catch {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Propose a feature" ariaLabel="Propose a feature" variant="sheet">
      {done ? (
        <div style={doneStyle} data-testid="propose-done">
          <span aria-hidden="true" style={{ fontSize: 'var(--font-size-xl)' }}>
            ✓
          </span>
          <h3 style={doneTitleStyle}>Idea added</h3>
          <p style={doneBodyStyle}>We&apos;ll review it and may publish it for everyone to upvote.</p>

          {/* "Back your request" — back your own just-proposed idea (proto's done-state offer). */}
          <div style={donateOfferStyle} data-testid="propose-donate">
            <span style={doHeadStyle}>
              <span aria-hidden="true">💜</span> Back your request
            </span>
            <p style={doDescStyle}>
              Put a few bucks behind it — it shows how much you want this. Only charged if we build
              it.
            </p>
            <button
              type="button"
              style={backOpenStyle}
              onClick={() => setBackOpen(true)}
              disabled={!proposedId}
              data-testid="propose-back-open"
            >
              Back this ›
            </button>
          </div>

          <button type="button" style={doneCloseStyle} onClick={onClose} data-testid="propose-done-close">
            Done
          </button>

          <BackIdeaDialog
            open={backOpen}
            ideaId={proposedId ?? ''}
            ideaTitle={text.trim() || 'Your request'}
            busy={backBusy}
            setBusy={setBackBusy}
            onClose={() => setBackOpen(false)}
            onConfirmed={() => {
              // The dialog owns the one-and-only success message; nothing to show
              // on the propose-done card. It stays open on its thank-you until the
              // user taps Done. No second "you back $X" message (anti-drift).
            }}
          />
        </div>
      ) : (
        <div data-testid="propose-form">
          <p style={subStyle}>
            Type your idea — we&apos;ll check if it&apos;s already been suggested, so your vote joins
            theirs instead of starting a duplicate.
          </p>
          <textarea
            style={inputStyle}
            rows={3}
            placeholder="e.g. A running total as I shop"
            value={text}
            onChange={(e) => onInput(e.target.value)}
            aria-label="Describe your feature idea"
            data-testid="propose-input"
          />

          {searched && (
            <div data-testid="propose-results">
              {matches.length > 0 ? (
                <>
                  <p style={resultsHeadStyle}>
                    Already suggested — upvote or back it instead of adding a duplicate:
                  </p>
                  <div style={matchListStyle}>
                    {matches.map((m) => (
                      <div key={m.id} data-testid={`propose-match-${m.id}`}>
                        <FeatureIdeaCardView idea={m} onVoted={onMatchVoted} />
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p style={resultsHeadStyle}>No similar idea yet — yours could be the first.</p>
              )}
            </div>
          )}

          {text.trim() !== '' && (
            <div style={addRowStyle}>
              <span style={addHintStyle}>
                {matches.length > 0 ? "Don't see yours?" : 'Add it so others can upvote it:'}
              </span>
              <button type="button" style={addBtnStyle} onClick={submit} disabled={busy} data-testid="propose-submit">
                Add as a new idea
              </button>
            </div>
          )}

          <p style={footStyle}>
            We curate ideas and publish them for everyone to upvote. Nothing&apos;s public until we
            do.
          </p>
        </div>
      )}
    </Modal>
  );
}
